export type MetricFormatUnit = 'USD' | 'ratio' | 'percent';

/** Compact currency (default USD, e.g. $12.3K). */
export const formatCurrencyCompact = (value: number, currency: 'USD' | string = 'USD'): string => {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency,
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

/** Compact currency with +/− for deltas (e.g. +$2.1K, −$300). */
export function formatCurrencySignedCompact(value: number, currency: 'USD' | string = 'USD'): string {
  if (!Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    notation: 'compact',
    signDisplay: value === 0 ? 'never' : 'exceptZero',
    maximumFractionDigits: 1,
  }).format(value);
}

/** One decimal, plain number. */
export const formatNumber1 = (value: number): string => {
  return Number.isFinite(value) ? value.toFixed(1) : '—';
};

/** One decimal with % (value is 0–100 scale). */
export function formatPercent1(value: number): string {
  if (!Number.isFinite(value)) return '—';
  return `${formatNumber1(value)}%`;
}

/** Route by unit for charts and tooltips. */
export function formatByUnit(v: number, unit: MetricFormatUnit): string {
  if (unit === 'USD') return formatCurrencyCompact(v, 'USD');
  if (unit === 'percent') return formatPercent1(v);
  return formatNumber1(v);
}

/** Signed compact value for deltas (USD uses compact currency with sign). */
export function formatSignedByUnit(v: number, unit: MetricFormatUnit): string {
  if (unit === 'USD') return formatCurrencySignedCompact(v, 'USD');
  if (unit === 'percent') {
    if (!Number.isFinite(v)) return '—';
    if (v === 0) return formatPercent1(0);
    const sign = v > 0 ? '+' : '−';
    return `${sign}${formatPercent1(Math.abs(v))}`;
  }
  if (!Number.isFinite(v)) return '—';
  if (v === 0) return formatNumber1(0);
  const sign = v > 0 ? '+' : '−';
  return `${sign}${formatNumber1(Math.abs(v))}`;
}

/**
 * Compact display for bullet charts and tooltips.
 * Delegates to {@link formatByUnit}.
 */
export function formatValue(v: number, unit: MetricFormatUnit): string {
  return formatByUnit(v, unit);
}
